import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '../../../molecules/Typography';
import { Colors } from '../../../../models/disciplines';
import ContentCard from '../../ContentCard';
import formatFileCategory from '../../../../commom/formatFileCategory';
import { useFinishMaterial } from '../../../../hooks/useFinishMaterial';
import {
  getViewedFromResponse,
  materialViewTypeFromCategory,
} from '../../../../models/contentView';
import { IResponseFavorite } from '../../../../../modules/Disciplines/hooks/useContentFavorite';

/**
 * Material "Conexões com Jubilut" vinculado por eixo temático. Difere dos
 * materiais relacionados por matéria (`Materials`): cada item carrega os
 * próprios `ancestors`/`subjects`, então a URL de abertura é montada por item.
 */
export interface ILessonAxisMaterial {
  id: string;
  title: string;
  slug: string;
  file_category?: string | null;
  done?: boolean;
  favorite?: boolean;
  subjects: { title: string; slug: string }[];
  ancestors?: {
    area?: { slug?: string };
    subarea?: { slug?: string };
  };
}

interface AxisMaterialsProps {
  materials: ILessonAxisMaterial[];
}

/** URL do visualizador de material (mantém o aluno no contexto de Conexões com Jubilut). */
function buildMaterialUrl(material: ILessonAxisMaterial): string {
  return `/conexoes-jubilut/material/${material.slug}`;
}

const AxisMaterials = ({ materials }: AxisMaterialsProps) => {
  const navigate = useNavigate();
  const { mutate: finishMaterial } = useFinishMaterial();

  // Estado local para refletir favoritar/marcar-visto de forma otimista; é
  // ressincronizado quando a lista recebida (prop) muda.
  const [items, setItems] = useState<ILessonAxisMaterial[]>(materials);
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);

  useEffect(() => {
    setItems(materials);
  }, [materials]);

  // Favoritar: o POST /favorites é disparado internamente pelo ContentCard; aqui
  // só refletimos o resultado para a estrela atualizar na hora.
  const onFavorite = (data: IResponseFavorite) => {
    if (!data.ok) return;
    setItems((prev) =>
      prev.map((it) =>
        it.id === data.targetId ? { ...it, favorite: data.insert === true } : it
      )
    );
  };

  // Marcar como visto: o endpoint é toggle; refletimos o status retornado.
  const onDone = (_id: string, slug: string) => {
    if (!slug) return;
    const material = items.find((it) => it.slug === slug);
    setLoadingSlug(slug);
    finishMaterial(
      {
        slug,
        type: materialViewTypeFromCategory(material?.file_category ?? undefined),
        origin: 'free_browsing',
      },
      {
        onSuccess: (res) => {
          const viewed = getViewedFromResponse(res);
          setItems((prev) =>
            prev.map((it) => (it.slug === slug ? { ...it, done: viewed } : it))
          );
        },
        onSettled: () => setLoadingSlug(null),
      }
    );
  };

  if (!items.length) return null;

  return (
    <div className='flex flex-col gap-md'>
      <Typography
        type='titleSection'
        size='h5'
        className='text-mono-color-dark'
      >
        DO EIXO TEMÁTICO
      </Typography>
      <div className='grid grid-cols-1 gap-md md:grid-cols-2 lg:grid-cols-3'>
        {items.map((material) => {
          const disciplineSlug = material.ancestors?.area?.slug;
          return (
            <ContentCard
              key={material.id}
              id={material.id}
              content='materiais'
              title={material.title}
              slug={material.slug}
              icon='article'
              done={material.done}
              favorite={material.favorite}
              color={disciplineSlug as Colors}
              subject={material.subjects?.[0]?.title}
              hasCallback
              callback={onFavorite}
              onDone={onDone}
              doneLoading={loadingSlug === material.slug}
              action={() => navigate(buildMaterialUrl(material))}
            >
              {material.file_category && (
                <Typography size='small'>
                  {formatFileCategory(material.file_category)}
                </Typography>
              )}
            </ContentCard>
          );
        })}
      </div>
    </div>
  );
};

export default AxisMaterials;
